import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { HospitalDto } from "@ambulance/shared";
import { useApi } from "../api";
import { Loader2, Search, AlertCircle, Lock } from "lucide-react";

type PcrRow = { _id: string; hospitalId?: string; hospitalName: string; caseNumber: string; date: string; unitCode: string; patientName: string; triageLevel?: string; chiefComplaint?: string; submittedAt: number };

export function PcrReportsPage() {
  const api = useApi();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  const [hospitalFilter, setHospitalFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const reportsQ = useQuery({
    queryKey: ["pcr-reports", hospitalFilter, api.token],
    queryFn: () => api.get<PcrRow[]>(`/api/v1/pcr-reports?limit=200${hospitalFilter ? `&hospitalId=${hospitalFilter}` : ""}`),
    enabled: Boolean(api.token),
  });
  const reports = reportsQ.data;

  const filtered = useMemo(() => {
    if (!reports) return [];
    if (!search) return reports;
    const q = search.toLowerCase();
    return reports.filter(
      (c) =>
        c.caseNumber?.toLowerCase().includes(q) ||
        c.hospitalName.toLowerCase().includes(q) ||
        c.patientName?.toLowerCase().includes(q) ||
        c.unitCode?.toLowerCase().includes(q)
    );
  }, [reports, search]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          Patient Care Reports
        </h1>
        {/* PCR is closed - this page is now a read-only archive, so there is no
            "New PCR" action. Only super admins ever reach it. */}
        <p className="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2">
          <Lock className="w-4 h-4 flex-shrink-0" />
          PCR is closed. This is a read-only archive of reports submitted before
          the closure - no new reports can be created.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by case, patient, unit, hospital…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
          />
        </div>
        <select
          value={hospitalFilter}
          onChange={(e) => setHospitalFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
        >
          <option value="">All my hospitals</option>
          {hospitals?.map((h) => (
            <option key={h._id} value={h._id}>
              {h.name}
            </option>
          ))}
        </select>
      </div>

      {reports === undefined ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">
            {reports.length === 0
              ? "No patient care reports submitted yet."
              : "No results match your filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Case #</th>
                    <th className="px-4 py-3">Patient</th>
                    <th className="px-4 py-3">Hospital</th>
                    <th className="px-4 py-3">Triage</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((c) => (
                    <tr key={c._id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="font-medium text-slate-900">{c.date}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(c.submittedAt).toLocaleString(undefined, { hour12: false })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">
                        {c.caseNumber || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.patientName || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.hospitalName}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                        {c.triageLevel || "—"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to="/pcr-reports/$id"
                          params={{ id: c._id }}
                          className="text-sm font-semibold text-[var(--hmg-red)]"
                        >
                          View &rarr;
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <Link
                key={c._id}
                to="/pcr-reports/$id"
                params={{ id: c._id }}
                className="block bg-white rounded-xl border border-slate-200 p-4 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-slate-900">
                    {c.patientName || "Patient"}
                  </div>
                  <div className="text-xs font-mono text-slate-500">
                    {c.caseNumber}
                  </div>
                </div>
                <div className="text-sm text-slate-700">{c.hospitalName}</div>
                <div className="text-xs text-slate-600">
                  {c.date} · {c.triageLevel || "—"}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
